/** @format */

import React, { useEffect, useState, useCallback, useRef } from "react";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Loader from "../../elements/Loader";
import { toast } from "react-toastify";
import { useDropzone } from 'react-dropzone';
import ApiService from "../../services/apiService";

const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    videoUrl: Yup.string().url("Must be a valid URL").required("Video URL is required"),
    thumbnailUrl: Yup.string().url("Must be a valid URL").nullable(),
    description: Yup.string().nullable(),
    pujaname: Yup.string().nullable()
});

// Separate component for video dropzone to follow React hooks rules
function VideoDropzone({ onUpload, uploading, currentVideoUrl }) {
    const onDrop = useCallback((acceptedFiles) => {
        onUpload(acceptedFiles);
    }, [onUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'video/*': ['.mp4', '.webm', '.ogg', '.mov']
        },
        maxFiles: 1
    });

    return (
        <div className="mb-3">
            <label className="form-label">Upload Video</label>
            <div
                {...getRootProps()}
                className={`dropzone dz-clickable ${isDragActive ? 'dz-drag-hover' : ''}`}
                style={{
                    border: '2px dashed #dee2e6',
                    borderRadius: '5px',
                    padding: '30px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#f8f9fa' : 'white'
                }}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <p>Uploading...</p>
                ) : isDragActive ? (
                    <p>Drop the video here...</p>
                ) : (
                    <p>Drag and drop a video file here, or click to select</p>
                )}
            </div>
            {currentVideoUrl && (
                <div className="mt-2">
                    <small className="text-muted">Current video: </small>
                    <a href={currentVideoUrl} target="_blank" rel="noopener noreferrer">
                        {currentVideoUrl.length > 60 ? currentVideoUrl.substring(0, 60) + '...' : currentVideoUrl}
                    </a>
                </div>
            )}
        </div>
    );
}

// Separate component for thumbnail dropzone
function ThumbnailDropzone({ onUpload, uploading, currentThumbnailUrl }) {
    const onDrop = useCallback((acceptedFiles) => {
        onUpload(acceptedFiles);
    }, [onUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif']
        },
        maxFiles: 1
    });

    return (
        <div className="mb-3">
            <label className="form-label">Upload Thumbnail</label>
            <div
                {...getRootProps()}
                className={`dropzone dz-clickable ${isDragActive ? 'dz-drag-hover' : ''}`}
                style={{
                    border: '2px dashed #dee2e6',
                    borderRadius: '5px',
                    padding: '30px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#f8f9fa' : 'white'
                }}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <p>Uploading...</p>
                ) : isDragActive ? (
                    <p>Drop the image here...</p>
                ) : (
                    <p>Drag and drop a thumbnail image here, or click to select</p>
                )}
            </div>
            {currentThumbnailUrl && (
                <div className="mt-2">
                    <img
                        src={currentThumbnailUrl}
                        alt="Thumbnail preview"
                        style={{ maxHeight: '100px', borderRadius: '5px' }}
                    />
                </div>
            )}
        </div>
    );
}

export default function AddTestimonial() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
    const formikRef = useRef(null);
    const [currentVideoUrl, setCurrentVideoUrl] = useState("");
    const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState("");
    const [pujaList, setPujaList] = useState([]);
    const [initialValues, setInitialValues] = useState({
        title: "",
        videoUrl: "",
        thumbnailUrl: "",
        description: "",
        pujaname: ""
    });

    const fetchPujaList = useCallback(async () => {
        try {
            const response = await ApiService.get('service/list');
            if (response?.data) {
                setPujaList(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch puja list:', err);
        }
    }, []);

    const getTestimonialById = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ApiService.get(`testimonial/${id}`);
            if (response?.data) {
                setInitialValues({
                    title: response.data.title || "",
                    videoUrl: response.data.videoUrl || "",
                    thumbnailUrl: response.data.thumbnailUrl || "",
                    description: response.data.description || "",
                    pujaname: response.data.pujaname || ""
                });
                setCurrentVideoUrl(response.data.videoUrl || "");
                setCurrentThumbnailUrl(response.data.thumbnailUrl || "");
            }
        } catch (err) {
            toast.error('Failed to fetch testimonial');
            navigate('/testimonials');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchPujaList();
        if (id) {
            getTestimonialById();
        }
    }, [id, fetchPujaList, getTestimonialById]);


    const onSubmit = async (values, { setSubmitting }) => {
        try {
            if (id) {
                await ApiService.put(`testimonial/${id}`, values);
                toast.success('Testimonial updated successfully');
            } else {
                await ApiService.post('testimonial', values);
                toast.success('Testimonial created successfully');
            }
            navigate('/testimonials');
        } catch (err) {
            toast.error(err?.response?.data?.msg || 'Failed to save testimonial');
        } finally {
            setSubmitting(false);
        }
    };

    const handleVideoUpload = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];

        // Use multipart upload for videos to support large files (up to 1GB)
        setUploading(true);
        try {
            // Dynamically import to avoid circular dependencies if any, or just keeping clean
            const { uploadFileMultipart } = await import('../../services/multipartUpload');

            const url = await uploadFileMultipart(file, (percent) => {
                // Optional: You could add a state for progress percentage here
                console.log(`Upload progress: ${percent}%`);
            });

            if (url) {
                if (formikRef.current) {
                    formikRef.current.setFieldValue('videoUrl', url);
                }
                setCurrentVideoUrl(url);
                toast.success('Video uploaded successfully');
            }
        } catch (err) {
            console.error('Video upload error:', err);
            toast.error('Failed to upload video');
        } finally {
            setUploading(false);
        }
    }, []);

    const handleThumbnailUpload = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append('file', file);

        setUploadingThumbnail(true);
        try {
            const response = await ApiService.post('upload/image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            });
            if (response?.url) {
                if (formikRef.current) {
                    formikRef.current.setFieldValue('thumbnailUrl', response.url);
                }
                setCurrentThumbnailUrl(response.url);
                toast.success('Thumbnail uploaded successfully');
            }
        } catch (err) {
            console.error('Thumbnail upload error:', err);
            toast.error('Failed to upload thumbnail');
        } finally {
            setUploadingThumbnail(false);
        }
    }, []);

    return (
        <div className="row">
            <div className="col-xl-12">
                {loading && <Loader />}
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title">{id ? 'Edit' : 'Add'} Testimonial</h4>
                    </div>
                    <div className="card-body">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            enableReinitialize
                            innerRef={formikRef}
                        >
                            {({ isSubmitting, values }) => (
                                <Form>
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className="mb-3">
                                                <label className="form-label">Title <span className="text-danger">*</span></label>
                                                <Field
                                                    type="text"
                                                    name="title"
                                                    className="form-control"
                                                    placeholder="Enter testimonial title"
                                                />
                                                <ErrorMessage name="title" component="div" className="text-danger" />
                                            </div>
                                        </div>

                                        <div className="col-lg-6">
                                            <div className="mb-3">
                                                <label className="form-label">Puja Name</label>
                                                <Field
                                                    as="select"
                                                    name="pujaname"
                                                    className="form-control"
                                                >
                                                    <option value="">-- Select Puja --</option>
                                                    {pujaList.map((puja) => (
                                                        <option key={puja.id} value={puja.title}>
                                                            {puja.title}
                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="pujaname" component="div" className="text-danger" />
                                            </div>
                                        </div>

                                        <div className="col-lg-6">
                                            <div className="mb-3">
                                                <label className="form-label">Video URL <span className="text-danger">*</span></label>
                                                <Field
                                                    type="text"
                                                    name="videoUrl"
                                                    className="form-control"
                                                    placeholder="Enter video URL or upload below"
                                                />
                                                <ErrorMessage name="videoUrl" component="div" className="text-danger" />
                                            </div>
                                        </div>

                                        <div className="col-lg-6">
                                            <VideoDropzone
                                                onUpload={handleVideoUpload}
                                                uploading={uploading}
                                                currentVideoUrl={values.videoUrl || currentVideoUrl}
                                            />
                                        </div>

                                        <div className="col-lg-6">
                                            <ThumbnailDropzone
                                                onUpload={handleThumbnailUpload}
                                                uploading={uploadingThumbnail}
                                                currentThumbnailUrl={values.thumbnailUrl || currentThumbnailUrl}
                                            />
                                        </div>

                                        <div className="col-lg-12">
                                            <div className="mb-3">
                                                <label className="form-label">Description</label>
                                                <Field
                                                    as="textarea"
                                                    name="description"
                                                    className="form-control"
                                                    placeholder="Enter testimonial description"
                                                    rows="4"
                                                />
                                                <ErrorMessage name="description" component="div" className="text-danger" />
                                            </div>
                                        </div>

                                        <div className="col-lg-12">
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isSubmitting || uploading || uploadingThumbnail}
                                                >
                                                    {isSubmitting ? 'Saving...' : (id ? 'Update' : 'Create')} Testimonial
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => navigate('/testimonials')}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
}

